import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { t } from "ttag";
import { flattenCompareRows, formatJsonValue, presenceForMissingKind } from "./deviceSyncCompare";
import { formatSyncError } from "./deviceSyncUtils";
import { PresenceIcon } from "./SyncStatusIndicators";
import useStyles from "./DeviceSync.style";

const rowKindLabel = (kind: string) => {
  if (kind === "missingOnDevice") {
    return t`Missing on device`;
  }
  if (kind === "missingInCloud") {
    return t`Missing in cloud`;
  }
  return t`Changed`;
};

const EntityDiffTable = ({
  compare,
  comparable,
  error,
  deviceSnapshot,
  cloudSnapshot,
  hideWhenEmpty
}: {
  compare: any;
  comparable?: boolean;
  error?: unknown;
  deviceSnapshot?: unknown;
  cloudSnapshot?: unknown;
  hideWhenEmpty?: boolean;
}) => {
  const classes = useStyles();
  const rows = flattenCompareRows(compare, deviceSnapshot, cloudSnapshot);

  if (comparable === false) {
    return (
      <Typography className={classes.empty}>
        {formatSyncError(error, t`Couldn't compare — no data from one side.`)}
      </Typography>
    );
  }

  if (rows.length === 0 && !compare) {
    return hideWhenEmpty ? null : <Typography className={classes.empty}>{t`No compare data yet.`}</Typography>;
  }

  if (rows.length === 0) {
    return hideWhenEmpty ? null : <Typography className={classes.empty}>{t`No differences.`}</Typography>;
  }

  return (
    <TableContainer className={classes.tableScroll}>
    <Table stickyHeader size="small" className={classes.entityTable}>
      <TableHead>
        <TableRow className={classes.tableHeadRow}>
          <TableCell className={classes.th}>{t`Id`}</TableCell>
          <TableCell className={classes.th}>{t`Kind`}</TableCell>
          <TableCell className={classes.th}>{t`Field`}</TableCell>
          <TableCell className={classes.th}>📱 {t`Device`}</TableCell>
          <TableCell className={classes.th}>☁️ {t`Cloud`}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow key={`${row.id}-${row.field || row.kind}-${index}`}>
            <TableCell className={classes.tdMono}>{row.id || "—"}</TableCell>
            <TableCell className={classes.td}>{rowKindLabel(row.kind)}</TableCell>
            <TableCell className={classes.td}>{row.field || "—"}</TableCell>
            {(() => {
              const presence = presenceForMissingKind(row.kind);
              if (presence) {
                return (
                  <>
                    <TableCell className={classes.td}>
                      <PresenceIcon present={presence.devicePresent} />
                    </TableCell>
                    <TableCell className={classes.td}>
                      <PresenceIcon present={presence.cloudPresent} />
                    </TableCell>
                  </>
                );
              }
              return (
                <>
                  <TableCell className={classes.tdMono}>{formatJsonValue(row.device)}</TableCell>
                  <TableCell className={classes.tdMono}>{formatJsonValue(row.cloud)}</TableCell>
                </>
              );
            })()}
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </TableContainer>
  );
};

export default EntityDiffTable;
